import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Chip,
  Divider,
  LinearProgress
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TimelineIcon from '@mui/icons-material/Timeline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PropTypes from 'prop-types';

// Frozen module-level singletons for empty default props (#943,
// react/no-object-type-as-default-prop). A `= []` / `= {}` default builds a NEW
// value on every render, so a memoised child or an effect dependency sees a change
// that never happened.
const EMPTY_ARRAY = Object.freeze([]);

/**
 * ProcessArea component displays completed thinking steps in an expandable section
 * with statistics and performance metrics.
 */
const ProcessArea = ({
  thinkingSteps = EMPTY_ARRAY,
  currentIteration = 0,
  maxIterations = 0,
  isVisible = true
}) => {
  const [expanded, setExpanded] = useState(false);
  const [totalDuration, setTotalDuration] = useState(0);

  // Calculate statistics when thinking steps change
  useEffect(() => {
    if (thinkingSteps.length > 0) {
      const firstStep = thinkingSteps[0];
      const lastStep = thinkingSteps[thinkingSteps.length - 1];

      if (firstStep.timestamp && lastStep.timestamp) {
        const startTime = new Date(firstStep.timestamp);
        const endTime = new Date(lastStep.timestamp);
        const duration = Math.floor((endTime - startTime) / 1000); // in seconds
        setTotalDuration(duration);
      }
    } else {
      setTotalDuration(0);
    }
  }, [thinkingSteps]);

  const getStepStats = () => {
    const stats = {
      total: thinkingSteps.length,
      toolUsage: thinkingSteps.filter(step => step.type === 'tool_use').length,
      observations: thinkingSteps.filter(step => step.type === 'observation').length,
      iterations: thinkingSteps.filter(step => step.type === 'iteration').length,
      // 'reasoning' is the backend's agent-reasoning thinking_type; count it as a
      // thought alongside 'general'/untyped so reasoning steps are not dropped.
      thoughts: thinkingSteps.filter(step => step.type === 'general' || step.type === 'reasoning' || !step.type).length
    };
    return stats;
  };

  const stats = getStepStats();

  if (!isVisible || thinkingSteps.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 2, px: 2 }}>
      <Paper
        elevation={1}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden'
        }}
      >
        {/* Process Header */}
        <Box
          sx={{
            p: 1.5,
            bgcolor: 'background.paper',
            borderBottom: expanded ? '1px solid' : 'none',
            borderBottomColor: 'divider',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
          onClick={() => setExpanded(!expanded)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <TimelineIcon sx={{ color: 'primary.main', mr: 1 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 'medium', mr: 2 }}>
              Process Summary
            </Typography>

            {/* Statistics Chips */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                size="small"
                label={`${stats.total} steps`}
                color="primary"
                variant="outlined"
                sx={{ height: 24, fontSize: '0.7rem' }}
              />
              {stats.toolUsage > 0 && (
                <Chip
                  size="small"
                  label={`${stats.toolUsage} tools`}
                  sx={{
                    height: 24,
                    fontSize: '0.7rem',
                    bgcolor: 'action.selected',
                    color: 'info.main'
                  }}
                />
              )}
              {totalDuration > 0 && (
                <Chip
                  size="small"
                  icon={<AccessTimeIcon fontSize="small" />}
                  label={`${totalDuration}s`}
                  sx={{
                    height: 24,
                    fontSize: '0.7rem',
                    bgcolor: 'action.selected',
                    color: 'primary.main'
                  }}
                />
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {currentIteration > 0 && maxIterations > 0 && (
              <Box sx={{ mr: 1, minWidth: 60 }}>
                <LinearProgress
                  variant="determinate"
                  value={(currentIteration / maxIterations) * 100}
                  sx={{ height: 4, borderRadius: 2 }}
                />
              </Box>
            )}
            <IconButton size="small" sx={{ p: 0.5 }}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* Expandable Content */}
        <Box sx={{
          maxHeight: expanded ? '400px' : 0,
          overflow: 'auto',
          transition: 'max-height 0.3s ease-in-out'
        }}>
          {expanded && (
            <Box sx={{ p: 2 }}>
              {/* Progress Indicator */}
              {currentIteration > 0 && maxIterations > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Iteration Progress
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {currentIteration} / {maxIterations}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(currentIteration / maxIterations) * 100}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              )}

              {/* Step Statistics */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                  Step Breakdown:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {stats.thoughts > 0 && (
                    <Chip
                      size="small"
                      icon={<CheckCircleIcon fontSize="small" />}
                      label={`${stats.thoughts} thoughts`}
                      sx={{
                        height: 28,
                        fontSize: '0.75rem',
                        bgcolor: 'action.hover',
                        color: 'text.secondary'
                      }}
                    />
                  )}
                  {stats.toolUsage > 0 && (
                    <Chip
                      size="small"
                      label={`${stats.toolUsage} tool calls`}
                      sx={{
                        height: 28,
                        fontSize: '0.75rem',
                        bgcolor: 'action.selected',
                        color: 'info.main'
                      }}
                    />
                  )}
                  {stats.observations > 0 && (
                    <Chip
                      size="small"
                      label={`${stats.observations} observations`}
                      sx={{
                        height: 28,
                        fontSize: '0.75rem',
                        bgcolor: 'success.light',
                        color: 'success.main'
                      }}
                    />
                  )}
                  {stats.iterations > 0 && (
                    <Chip
                      size="small"
                      label={`${stats.iterations} iterations`}
                      sx={{
                        height: 28,
                        fontSize: '0.75rem',
                        bgcolor: 'warning.light',
                        color: 'warning.main'
                      }}
                    />
                  )}
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Completed Steps Timeline */}
              <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                Completed Steps:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {thinkingSteps.map((step, index) => (
                  <Box
                    key={step.id || index}
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: 'background.default',
                      border: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 'bold',
                        color: 'primary.main',
                        minWidth: '20px',
                        mt: 0.5
                      }}
                    >
                      {index + 1}
                    </Typography>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                        {step.type === 'tool_use' ? '🔧 Tool Usage' :
                         step.type === 'observation' ? '👁️ Observation' :
                         step.type === 'iteration' ? '🔄 Iteration' :
                         step.type === 'reasoning' ? '💭 Reasoning' : '💭 Thought'}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.8rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}
                      >
                        {/*
                          Render as plain text (React escapes it) — step.content is
                          model/LLM-derived markdown, so dangerouslySetInnerHTML was
                          both an XSS vector and showed literal markup. Strip simple
                          bold markers for readability.
                        */}
                        {(() => {
                          const text = (step.content || '').replace(/\*\*/g, '');
                          return text.length > 100 ? text.substring(0, 100) + '...' : text;
                        })()}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

ProcessArea.propTypes = {
  thinkingSteps: PropTypes.array,
  currentIteration: PropTypes.number,
  maxIterations: PropTypes.number,
  isVisible: PropTypes.bool,
  onToggleVisibility: PropTypes.func
};

export default ProcessArea;